import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { auth, googleProvider, isFirebaseConfigured, db } from '../firebase/config';
import { syncUserProfile } from '../services/trackingService';
import { trackUserLogin, trackUserRegistered, trackSignupFromTeaser, consumeTeaserIntent } from '../services/analyticsService';
import { trackAdsCadastro, trackAdsAssinaturaConfirmada } from '../services/gadsConversions';
import { doc, onSnapshot } from 'firebase/firestore';

interface AuthContextType {
  user: any | null;
  loading: boolean;
  signInGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      console.error("Firebase não está configurado. Verifique as variáveis de ambiente.");
      setLoading(false);
      return;
    }

    // Listener de autenticação do Firebase
    const unsubscribe = auth.onAuthStateChanged(async (currentUser: any) => {
      const wasLoggedOut = !user;
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        // syncUserProfile retorna true se criou o doc agora (novo cadastro)
        // É a fonte de verdade — não há race condition aqui pois é ela quem cria o doc
        const isNewUser = await syncUserProfile(currentUser);
        if (isNewUser) {
          trackUserRegistered({ method: 'google' });
          // B1: atribuição teaser→cadastro — flag gravada no clique de destrave
          if (consumeTeaserIntent()) {
            trackSignupFromTeaser();
          }
          trackAdsCadastro();
          // Sinaliza ao App.tsx para exibir o popup de boas-vindas do cadastro grátis.
          window.dispatchEvent(new CustomEvent('app-welcome-free'));
        } else if (wasLoggedOut) {
          // Track login apenas para usuários já existentes
          trackUserLogin({ method: 'google' });
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Listener Firestore: detecta mudança de plano free → pago para disparar conversão
  const lastTrackedPlanRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !isFirebaseConfigured) return;

    const userDocRef = doc(db, 'users', user.uid);
    const unsubPlan = onSnapshot(userDocRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const plan = data?.subscription?.plan;
      const planActivatedAt = data?.subscription?.planActivatedAt;

      if ((plan === 'monthly' || plan === 'quarterly') && plan !== lastTrackedPlanRef.current) {
        // Dispara apenas para ativações recentes (últimas 24h).
        // Janela em ms evita comparação getDate() UTC vs local, que perdia
        // conversões quando pagamento e retorno caíam em dias diferentes do fuso.
        if (planActivatedAt) {
          const activatedMs = new Date(planActivatedAt).getTime();
          const ageMs = Date.now() - activatedMs;
          const isRecent = ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000;

          if (isRecent) {
            // Verifica se o App.tsx já disparou via URL de retorno do MP
            // para não contar a mesma assinatura duas vezes no Google Ads.
            if (!sessionStorage.getItem('gads_assinatura_disparada')) {
              trackAdsAssinaturaConfirmada(plan);
              sessionStorage.setItem('gads_assinatura_disparada', 'true');
            }
            lastTrackedPlanRef.current = plan;
          }
        }
      }
    });

    return () => unsubPlan();
  }, [user]);

  const signInGoogle = async () => {
    if (!isFirebaseConfigured) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'error', title: 'Erro de Configuração', message: 'Firebase não está configurado corretamente. Contate o suporte.' } }));
      return;
    }

    try {
      await auth.signInWithPopup(googleProvider);
      // A detecção de novo usuário e o disparo de trackAdsCadastro acontecem
      // no onAuthStateChanged via syncUserProfile (sem race condition).
    } catch (error: any) {
      console.error("Erro ao logar:", error);

      // Erro: Domínio não autorizado
      if (error.code === 'auth/unauthorized-domain' || error.message?.includes('unauthorized domain')) {
        const currentDomain = window.location.hostname;
        window.dispatchEvent(new CustomEvent('app-toast', {
          detail: {
            type: 'error',
            title: 'Domínio não autorizado',
            message: `Este domínio (${currentDomain}) não está configurado no Firebase. Acesse Firebase Console > Authentication > Settings e adicione "${currentDomain}" em Authorized Domains.`
          }
        }));
        return;
      }

      // Erro: Popup bloqueado
      if (error.code === 'auth/popup-blocked') {
        window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'warning', title: 'Popup bloqueado', message: 'Por favor, permita popups para este site.' } }));
        return;
      }

      // Erro: Popup fechado pelo usuário
      if (error.code === 'auth/popup-closed-by-user') {
        // Não mostra erro, usuário apenas cancelou
        return;
      }

      // Outros erros
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'error', title: 'Erro ao fazer login', message: error.message || 'Tente novamente.' } }));
    }
  };

  const logout = async () => {
    try {
      if (auth.currentUser) {
        await auth.signOut();
      }
    } catch (e) {
      console.error("Erro ao deslogar:", e);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signInGoogle, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
